/**
 * Modal "Registrar ausencia" (008 · T076 · US4; contracts/ui.md §1.5, FR de ausencias).
 *
 * Trabajador (Combobox de activos del alcance), tipo, "Todo el día" o "Por horas" (solo Permiso),
 * rango de días u horas del día, y nota opcional. La validación es la del contrato
 * (`CreateAbsenceBodySchema`: rango ≤ 366 días, horas solo en un permiso de un día y inicio < fin),
 * cuyos issues `custom` ya traen su texto es-PE.
 *
 * Errores de la API: el solape de día completo llega como `400 VALIDATION_ERROR` en `start_day`
 * con el rango con que choca ("Se cruza con otra ausencia del 5 al 16 de octubre") y se muestra
 * bajo el campo "Desde"; `409 PERIOD_CLOSED` también va a ese campo. El resto, en un toast.
 */
import { useMemo, useState } from 'react';

import {
  AbsenceTypeSchema,
  CreateAbsenceBodySchema,
  type AbsenceType,
  type CreateAbsenceBody,
} from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/combobox';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input, Textarea } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { t } from '../../i18n';
import { scheduleErrorMessage, useWorkerOptions } from '../schedules/api';
import { formatLocalDay } from '../schedules/format';
import { readOnlyProps, useReadOnly } from '../shell/use-read-only';

import { absenceTypeLabel } from './absence-type-badge';
import { useCreateAbsence } from './api';

import type { ApiError } from '../../api/client';

export type AbsenceMode = 'FULL_DAY' | 'HOURS';

export interface AbsenceFormValues {
  worker_id: string;
  type: AbsenceType;
  mode: AbsenceMode;
  start_day: string;
  end_day: string;
  start_time: string;
  end_time: string;
  note: string;
}

type AbsenceField =
  'worker_id' | 'type' | 'start_day' | 'end_day' | 'start_time' | 'end_time' | 'note';

const FIELDS: readonly AbsenceField[] = [
  'worker_id',
  'type',
  'start_day',
  'end_day',
  'start_time',
  'end_time',
  'note',
];

export const NOTE_MAX = 300;

function isField(value: string): value is AbsenceField {
  return (FIELDS as readonly string[]).includes(value);
}

/** Cuerpo de `POST /absences` desde el formulario (sin validar). */
export function absenceBodyFrom(values: AbsenceFormValues): Record<string, unknown> {
  const hours = values.type === 'PERMISSION' && values.mode === 'HOURS';
  const note = values.note.trim();
  return {
    worker_id: values.worker_id,
    type: values.type,
    start_day: values.start_day,
    // Por horas es un solo día: el fin es el mismo día.
    end_day: hours ? values.start_day : values.end_day,
    ...(hours ? { start_time: values.start_time, end_time: values.end_time } : {}),
    ...(note ? { note } : {}),
  };
}

/** Errores por campo de un `safeParse` del contrato. */
export function absenceIssues(
  issues: readonly { path: readonly PropertyKey[]; code: string; message: string }[],
): Partial<Record<AbsenceField, string>> {
  const errors: Partial<Record<AbsenceField, string>> = {};
  for (const issue of issues) {
    const field = String(issue.path[0] ?? '');
    if (!isField(field) || errors[field]) {
      continue;
    }
    errors[field] = issue.code === 'custom' ? issue.message : t(`absences.form.errors.${field}`);
  }
  return errors;
}

/** Errores de la API que tienen campo: issues de validación y `PERIOD_CLOSED` (en "Desde"). */
export function absenceApiErrors(error: ApiError): Partial<Record<AbsenceField, string>> {
  const errors: Partial<Record<AbsenceField, string>> = {};
  const issues = error.details?.['issues'];
  if (error.is('VALIDATION_ERROR') && Array.isArray(issues)) {
    for (const issue of issues as { path?: unknown; message?: unknown }[]) {
      const path = typeof issue.path === 'string' ? issue.path.split('.')[0] : undefined;
      if (path && isField(path) && !errors[path] && typeof issue.message === 'string') {
        errors[path] = issue.message;
      }
    }
  }
  if (error.is('PERIOD_CLOSED')) {
    errors.start_day = scheduleErrorMessage(error);
  }
  return errors;
}

function initialValues(today: string): AbsenceFormValues {
  return {
    worker_id: '',
    type: 'VACATION',
    mode: 'FULL_DAY',
    start_day: today,
    end_day: today,
    start_time: '',
    end_time: '',
    note: '',
  };
}

export interface AbsenceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hoy en la zona de la empresa: valor inicial de las fechas. */
  today: string;
}

/** El formulario se monta al abrir: cada apertura empieza limpia. */
export function AbsenceFormDialog({ open, onOpenChange, today }: AbsenceFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? <AbsenceFormContent onOpenChange={onOpenChange} today={today} /> : null}
    </Dialog>
  );
}

function AbsenceFormContent({ onOpenChange, today }: Omit<AbsenceFormDialogProps, 'open'>) {
  const { toast } = useToast();
  const { readOnly } = useReadOnly();
  const create = useCreateAbsence();
  const workers = useWorkerOptions({ status: 'ACTIVE' });
  const [values, setValues] = useState<AbsenceFormValues>(() => initialValues(today));
  const [errors, setErrors] = useState<Partial<Record<AbsenceField, string>>>({});

  const typeOptions = useMemo(
    () => AbsenceTypeSchema.options.map((type) => ({ value: type, label: absenceTypeLabel(type) })),
    [],
  );

  const hours = values.type === 'PERMISSION' && values.mode === 'HOURS';

  function update(patch: Partial<AbsenceFormValues>) {
    setValues((current) => ({ ...current, ...patch }));
  }

  function submit() {
    const parsed = CreateAbsenceBodySchema.safeParse(absenceBodyFrom(values));
    if (!parsed.success) {
      setErrors(absenceIssues(parsed.error.issues));
      return;
    }
    setErrors({});
    const body: CreateAbsenceBody = parsed.data;
    create.mutate(body, {
      onSuccess: (saved) => {
        toast({
          title: t('absences.toast.created', {
            type: absenceTypeLabel(saved.type, saved.partial),
            worker: saved.worker.name,
          }),
          variant: 'success',
        });
        onOpenChange(false);
      },
      onError: (error) => {
        const fieldErrors = absenceApiErrors(error);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
        toast({ title: scheduleErrorMessage(error), variant: 'error' });
      },
    });
  }

  return (
    <DialogContent
      title={t('absences.form.title')}
      description={t('absences.form.description')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            type="submit"
            form="absence-form"
            loading={create.isPending}
            loadingText={t('common.states.saving')}
            {...readOnlyProps(readOnly)}
          >
            {t('absences.form.submit')}
          </Button>
        </>
      }
    >
      <form
        id="absence-form"
        data-testid="absence-form"
        className="flex flex-col gap-fm-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label={t('absences.form.worker')} required error={errors.worker_id}>
          {(control) => (
            <Combobox
              id={control.id}
              aria-describedby={control['aria-describedby']}
              aria-required={control['aria-required']}
              invalid={errors.worker_id !== undefined}
              value={values.worker_id}
              onValueChange={(value) => update({ worker_id: value })}
              options={workers.options}
              placeholder={t('absences.form.workerPlaceholder')}
              searchPlaceholder={t('absences.form.workerSearch')}
            />
          )}
        </Field>

        <Field label={t('absences.form.type')} required error={errors.type}>
          {(control) => (
            <Select
              id={control.id}
              aria-describedby={control['aria-describedby']}
              value={values.type}
              onValueChange={(value) =>
                update({ type: AbsenceTypeSchema.parse(value), mode: 'FULL_DAY' })
              }
              options={typeOptions}
            />
          )}
        </Field>

        {values.type === 'PERMISSION' ? (
          <fieldset className="flex flex-col gap-fm-2">
            <legend className="text-fm-sm font-medium text-fm-text">
              {t('absences.form.mode')}
            </legend>
            <div className="flex flex-wrap gap-fm-4">
              {(['FULL_DAY', 'HOURS'] as const).map((mode) => (
                <label
                  key={mode}
                  className="flex min-h-fm-touch items-center gap-fm-2 text-fm-sm text-fm-text"
                >
                  <input
                    type="radio"
                    name="absence-mode"
                    value={mode}
                    checked={values.mode === mode}
                    onChange={() => update({ mode })}
                    className="size-4 accent-[var(--fm-primary)]"
                  />
                  {t(`absences.form.modes.${mode}`)}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        {hours ? (
          <>
            <Field label={t('absences.form.day')} required error={errors.start_day}>
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  value={values.start_day}
                  onChange={(event) => update({ start_day: event.target.value })}
                />
              )}
            </Field>
            <div className="grid grid-cols-2 gap-fm-3">
              <Field label={t('absences.form.startTime')} required error={errors.start_time}>
                {(control) => (
                  <Input
                    {...control}
                    type="time"
                    step={60}
                    value={values.start_time}
                    onChange={(event) => update({ start_time: event.target.value })}
                  />
                )}
              </Field>
              <Field
                label={t('absences.form.endTime')}
                required
                error={errors.end_time ?? errors.end_day}
              >
                {(control) => (
                  <Input
                    {...control}
                    type="time"
                    step={60}
                    value={values.end_time}
                    onChange={(event) => update({ end_time: event.target.value })}
                  />
                )}
              </Field>
            </div>
            <p className="-mt-fm-2 text-fm-xs text-fm-text-secondary">
              {t('absences.form.hoursHelp')}
            </p>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-fm-3">
            <Field label={t('absences.form.startDay')} required error={errors.start_day}>
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  value={values.start_day}
                  onChange={(event) => update({ start_day: event.target.value })}
                />
              )}
            </Field>
            <Field label={t('absences.form.endDay')} required error={errors.end_day}>
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  min={values.start_day || undefined}
                  value={values.end_day}
                  onChange={(event) => update({ end_day: event.target.value })}
                />
              )}
            </Field>
          </div>
        )}

        <Field
          label={t('absences.form.note')}
          help={t('absences.form.noteCount', { count: values.note.length, max: NOTE_MAX })}
          error={errors.note}
        >
          {(control) => (
            <Textarea
              {...control}
              maxLength={NOTE_MAX}
              value={values.note}
              onChange={(event) => update({ note: event.target.value })}
            />
          )}
        </Field>

        {!hours && values.start_day && values.end_day && values.start_day <= values.end_day ? (
          <p className="text-fm-xs text-fm-text-secondary" data-testid="absence-form-range">
            {t('absences.form.rangeSummary', {
              from: formatLocalDay(values.start_day),
              to: formatLocalDay(values.end_day),
            })}
          </p>
        ) : null}
      </form>
    </DialogContent>
  );
}
